import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import "./css/registerform.css";
import axiosInstance from "../utils/axiosInstance";
import { State } from "country-state-city";
import Loader from "./Loader/Loader";
import {
  showSuccessToast,
  showErrorToast,
  showCenterErrorToast,
} from "./toast";

const usStates = State.getStatesOfCountry("US");

const generateCaptcha = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let captcha = "";
  for (let i = 0; i < 6; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return captcha;
};

const RegisterForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    experience: "",
    city: "",
    state: "",
    zip: "",
    trainingType: "",
    remarks: "",
    formType: "sntraining",
    recordType: "SN student",
    stage: "stage1",
  });

  const [errors, setErrors] = useState({});
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");

  /* ---------- INPUT CHANGE ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Zip code: numbers only
    if (name === "zip" && !/^\d*$/.test(value)) return;

    // Experience: prevent negative values
    if (name === "experience" && Number(value) < 0) return;

    setFormData({ ...formData, [name]: value });

    // CLEAR error for this field when user types
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /* ---------- VALIDATION ---------- */
  const validate = () => {
    let newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email))
      newErrors.email = "Invalid email format";

    if (!formData.phone) newErrors.phone = "Phone number is required";
    else if (
      !/^(\+1\s?)?(\(?\d{3}\)?[\s.-]?){1}\d{3}[\s.-]?\d{4}$/.test(
        formData.phone,
      )
    ) {
      newErrors.phone = "Enter valid phone number";
    }

    if (!formData.experience) newErrors.experience = "Experience is required";

    if (!formData.city.trim()) newErrors.city = "City is required";

    if (!formData.state) newErrors.state = "State is required";

    if (!formData.zip) newErrors.zip = "Zip code is required";

    if (captchaInput !== captcha) newErrors.captcha = "Captcha does not match";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApiError = (error) => {
    if (!error.response) {
      showErrorToast("Server not reachable. Please try again later.");
      return;
    }

    const { status, data } = error.response;

    //409 Conflict
    if (status === 409) {
      showCenterErrorToast("Email already exists");
      return;
    }

    // fallback
    showErrorToast("Something went wrong. Please try again.");
  };

  /* ---------- SUBMIT ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);

      const response = await axiosInstance.post(
        "/api/sntranning/register",
        formData,
      );

      showSuccessToast("Registration submitted successfully!");

      // reset
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        experience: "",
        city: "",
        state: "",
        zip: "",
        remarks: "",
        trainingType: "",
      });
      setCaptcha(generateCaptcha());
      setCaptchaInput("");
      setErrors({});
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
      setErrors({});
    }
  };

  return (
    <div className="contact-section">
      <div className="contact-container row">
        {loading && <Loader />}
        <div className="col-xxl-5 col-xl-5 col-lg-5 col-md-12 col-sm-12 custom-padding ">
          <div className="registerImg w-100">
            <div className="registerFlyer-container"></div>
          </div>
        </div>

        <div className="col-xxl-7 col-xl-7 col-lg-7 col-md-12 col-sm-12 custom-padding ">
          <div className="card contact-form w-100">
            <div className="text-center">
              <div className="content-heading1 text-header">
                Specialized ServiceNow Training - Get Certified!!
              </div>
              <p className="text-muted">Start Date : February 16, 2026</p>
            </div>
            <div className="text-left">
              <h4 className="registerHead">Registration Form</h4>
            </div>

            <Form
              onSubmit={handleSubmit}
              className="p-3 border rounded shadow formbg"
            >
              <div className="row mb-2">
                <div className="col-md-6 formResponsive">
                  <label>
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  <small className="text-danger">{errors.firstName}</small>
                </div>

                <div className="col-md-6">
                  <label>
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                  <small className="text-danger">{errors.lastName}</small>
                </div>
              </div>

              {/* Email & Phone */}
              <div className="row mb-2">
                <div className="col-md-6 formResponsive">
                  <label>
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <small className="text-danger">{errors.email}</small>
                </div>

                <div className="col-md-6">
                  <label>
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  <small className="text-danger">{errors.phone}</small>
                </div>
              </div>

              <div className=" mb-2">
                {/* <div className="col-md-6 formResponsive"> */}
                <label>
                  Years of IT or Functional Experience{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                />
                <small className="text-danger">{errors.experience}</small>
              </div>

              {/* Experience & City */}
              <div className="row mb-2">
                <div className="col-md-6 formResponsive">
                  <label>
                    Location (City) <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                  <small className="text-danger">{errors.city}</small>
                </div>
                <div className="col-md-6 formResponsive">
                  <label>
                    State <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select State --</option>
                    {usStates.map((state) => (
                      <option key={state.name} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </select>

                  <small className="text-danger">{errors.state}</small>
                </div>
              </div>

              {/* State & Zip */}
              <div className="row mb-2">
                <div className="col-md-6 formResponsive">
                  <label>
                    Zip Code <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                  />
                  <small className="text-danger">{errors.zip}</small>
                </div>

                <div className="col-md-6 ">
                  <label>
                    Training Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="trainingType"
                    value={formData.trainingType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Training Type --</option>
                    <option value="Technical">Technical</option>
                    <option value="Functional">Functional</option>
                    <option value="User">User</option>
                  </select>
                  <small className="text-danger">{errors.trainingType}</small>
                </div>
              </div>

              {/* Remarks */}
              <div className="mb-2">
                <label>Remarks (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>

              {/* Captcha */}
              <div className="mb-2">
                <label>Captcha</label>
                <div className="d-flex align-items-center gap-3">
                  <input
                    className="form-control text-center"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    style={{ maxWidth: "200px" }}
                  />
                  <strong className="px-3 py-2 bg-light border rounded">
                    {captcha}
                  </strong>
                  <Button
                    type="button"
                    variant="light"
                    onClick={() => {
                      setCaptcha(generateCaptcha());
                      setCaptchaInput("");
                    }}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                  </Button>
                </div>
                <small className="text-danger">{errors.captcha}</small>
              </div>

              <Button type="submit" className="w-100 btnmesg">
                Register
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
